import { Injectable } from '@angular/core';
import { DocumentNode, FetchResult } from '@apollo/client/core';
import { DigitaiCreatePotentialIssue, DigitaiPotentialIssue, DigitaiUpdatePotentialIssueById, DigitaiUpdatePotentialIssues } from '@apps/digitai/digitai.types';
import { createMutation, deleteByIdMutation, deleteMutation, fields, findByIdQuery, findQuery, getQuery, paginationQuery, updateByIdMutation, updateMutation } from '@apps/digitai/potential-issue';
import { GraphQLHeaders, GraphQLService, GridData, parseGqlFields, QueryStatement } from '@aurora';
import { BehaviorSubject, first, map, Observable, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class PotentialIssueService
{
    paginationSubject$: BehaviorSubject<GridData<DigitaiPotentialIssue> | null> = new BehaviorSubject(null);
    potentialIssueSubject$: BehaviorSubject<DigitaiPotentialIssue | null> = new BehaviorSubject(null);
    potentialIssuesSubject$: BehaviorSubject<DigitaiPotentialIssue[] | null> = new BehaviorSubject(null);

    // scoped subjects
    paginationScoped: { [key: string]: BehaviorSubject<GridData<DigitaiPotentialIssue> | null>; } = {};
    potentialIssueScoped: { [key: string]: BehaviorSubject<DigitaiPotentialIssue | null>; } = {};
    potentialIssuesScoped: { [key: string]: BehaviorSubject<DigitaiPotentialIssue[] | null>; } = {};

    constructor(
        private readonly graphqlService: GraphQLService,
    ) {}

    /**
    * Getters
    */
    get pagination$(): Observable<GridData<DigitaiPotentialIssue>>
    {
        return this.paginationSubject$.asObservable();
    }

    get potentialIssue$(): Observable<DigitaiPotentialIssue>
    {
        return this.potentialIssueSubject$.asObservable();
    }

    get potentialIssues$(): Observable<DigitaiPotentialIssue[]>
    {
        return this.potentialIssuesSubject$.asObservable();
    }

    // allows to store different types of pagination under different scopes this allows us
    // to have multiple observables with different streams of pagination data.
    setScopePagination(scope: string, pagination: GridData<DigitaiPotentialIssue>): void
    {
        if (this.paginationScoped[scope])
        {
            this.paginationScoped[scope].next(pagination);
            return;
        }
        // create new subject if not exist
        this.paginationScoped[scope] = new BehaviorSubject(pagination);
    }

    // get pagination observable by scope
    getScopePagination(scope: string): Observable<GridData<DigitaiPotentialIssue>>
    {
        if (this.paginationScoped[scope]) return this.paginationScoped[scope].asObservable();
        return null;
    }

    setScopePotentialIssue(scope: string, object: DigitaiPotentialIssue): void
    {
        if (this.potentialIssueScoped[scope])
        {
            this.potentialIssueScoped[scope].next(object);
            return;
        }
        // create new subject if not exist
        this.potentialIssueScoped[scope] = new BehaviorSubject(object);
    }

    getScopePotentialIssue(scope: string): Observable<DigitaiPotentialIssue>
    {
        if (this.potentialIssueScoped[scope]) return this.potentialIssueScoped[scope].asObservable();
        return null;
    }

    setScopePotentialIssues(scope: string, objects: DigitaiPotentialIssue[]): void
    {
        if (this.potentialIssuesScoped[scope])
        {
            this.potentialIssuesScoped[scope].next(objects);
            return;
        }
        // create new subject if not exist
        this.potentialIssuesScoped[scope] = new BehaviorSubject(objects);
    }

    getScopePotentialIssues(scope: string): Observable<DigitaiPotentialIssue[]>
    {
        if (this.potentialIssuesScoped[scope]) return this.potentialIssuesScoped[scope].asObservable();
        return null;
    }

    pagination(
        {
            graphqlStatement = paginationQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<GridData<DigitaiPotentialIssue>>
    {
        // get result, map ang throw data across observable
        return this.graphqlService
            .client()
            .watchQuery<{ pagination: GridData<DigitaiPotentialIssue>; }>({
                query    : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data.pagination),
                tap(pagination => scope ? this.setScopePagination(scope, pagination) : this.paginationSubject$.next(pagination)),
            );
    }

    findById(
        {
            graphqlStatement = findByIdQuery,
            id = '',
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiPotentialIssue;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiPotentialIssue;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopePotentialIssue(scope, data.object) : this.potentialIssueSubject$.next(data.object)),
            );
    }

    find(
        {
            graphqlStatement = findQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiPotentialIssue;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiPotentialIssue;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopePotentialIssue(scope, data.object) : this.potentialIssueSubject$.next(data.object)),
            );
    }

    get(
        {
            graphqlStatement = getQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        objects: DigitaiPotentialIssue[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                objects: DigitaiPotentialIssue[];
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopePotentialIssues(scope, data.objects) : this.potentialIssuesSubject$.next(data.objects)),
            );
    }

    create<T>(
        {
            graphqlStatement = createMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiCreatePotentialIssue;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    updateById<T>(
        {
            graphqlStatement = updateByIdMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdatePotentialIssueById;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    update<T>(
        {
            graphqlStatement = updateMutation,
            object = null,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdatePotentialIssues;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    deleteById<T>(
        {
            graphqlStatement = deleteByIdMutation,
            id = '',
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    delete<T>(
        {
            graphqlStatement = deleteMutation,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }
}
